import React from 'react';
import Icon from '../../../components/AppIcon';

const ExportStatsPanel = ({ stats }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatPercentage = (value, total) => {
    if (total === 0) return '0%';
    return Math.round((value / total) * 100) + '%';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Export Statistics</h3>
        <Icon name="BarChart3" size={20} className="text-muted-foreground" />
      </div>
      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-primary">{stats?.totalExports}</div>
            <div className="text-sm text-muted-foreground">Total Exports</div>
          </div>
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-success">{formatFileSize(stats?.totalSize)}</div>
            <div className="text-sm text-muted-foreground">Total Size</div>
          </div>
        </div>

        {/* File Format Distribution */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">File Format Distribution</h4>
          <div className="space-y-3">
            {stats?.formatDistribution?.map((format) => (
              <div key={format?.type} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={format?.type === 'CSV' ? 'FileText' : 'FileSpreadsheet'} 
                    size={16} 
                    className="text-muted-foreground" 
                  />
                  <span className="text-sm text-foreground">{format?.type}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: formatPercentage(format?.count, stats?.totalExports) }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8 text-right">
                    {format?.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Export Types */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Export Types</h4>
          <div className="space-y-3">
            {stats?.typeDistribution?.map((type) => (
              <div key={type?.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={type?.name === 'Sage 50' ? 'Database' : 'FileDown'} 
                    size={16} 
                    className="text-muted-foreground" 
                  />
                  <span className="text-sm text-foreground">{type?.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-accent transition-all duration-300"
                      style={{ width: formatPercentage(type?.count, stats?.totalExports) }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8 text-right">
                    {type?.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Recent Activity</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Last 7 days</span>
              <span className="text-foreground font-medium">{stats?.recentActivity?.last7Days}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Last 30 days</span>
              <span className="text-foreground font-medium">{stats?.recentActivity?.last30Days}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Average per day</span>
              <span className="text-foreground font-medium">{stats?.recentActivity?.averagePerDay}</span>
            </div>
          </div>
        </div>

        {/* Storage Usage */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Storage Usage</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Used</span>
              <span className="text-sm text-foreground font-medium">
                {formatFileSize(stats?.storageUsage?.used)}
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-warning transition-all duration-300"
                style={{ width: `${(stats?.storageUsage?.used / stats?.storageUsage?.limit) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>{formatFileSize(stats?.storageUsage?.limit)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportStatsPanel;